import * as React from 'react';
import { cn } from './lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';

let echarts;
try { echarts = require('echarts/core'); } catch (_) { /* lazy */ }
let HeatmapChart, GridComponent, TooltipComponent, VisualMapComponent, CanvasRenderer;
try {
  HeatmapChart = require('echarts/charts').HeatmapChart;
  GridComponent = require('echarts/components').GridComponent;
  TooltipComponent = require('echarts/components').TooltipComponent;
  VisualMapComponent = require('echarts/components').VisualMapComponent;
  CanvasRenderer = require('echarts/renderers').CanvasRenderer;
  echarts.use([HeatmapChart, GridComponent, TooltipComponent, VisualMapComponent, CanvasRenderer]);
} catch (_) { /* optional */ }

/**
 * ECharts heatmap wrapper for complaint aging and similar grids.
 *
 * Props:
 *   xLabels: string[]         — column labels (e.g., aging buckets)
 *   yLabels: string[]         — row labels (e.g., facility names)
 *   data:    [x, y, value][]  — heatmap data points
 *   colorRange: [low, high]   — hex colors for visual map
 *   height: number            — chart height in px
 */
function NNHeatmapWidget({ xLabels, yLabels, data, colorRange = ['#22c55e', '#ef4444'], height = 300, className }) {
  const chartRef = React.useRef(null);
  const instanceRef = React.useRef(null);
  const theme = useTheme();
  const ct = getChartTheme(theme);

  React.useEffect(() => {
    if (!echarts || !chartRef.current) return;
    // Derive tc inside useEffect to avoid dependency instability
    const tc = { textColor: ct.axisLabelColor, borderColor: ct.tooltipBorder, bg: ct.tooltipBg };
    const chart = echarts.init(chartRef.current);
    instanceRef.current = chart;

    const maxVal = Math.max(...data.map((d) => d[2]), 1);

    chart.setOption({
      tooltip: {
        position: 'top',
        formatter: (p) => `${yLabels[p.value[1]]}<br/>${xLabels[p.value[0]]}: <b>${p.value[2]}</b>`,
        backgroundColor: tc.bg,
        borderColor: tc.borderColor,
        textStyle: { color: tc.textColor, fontSize: 11 },
      },
      grid: { left: 120, right: 20, top: 10, bottom: 40 },
      xAxis: {
        type: 'category', data: xLabels, splitArea: { show: true },
        axisLabel: { color: tc.textColor, fontSize: 11 },
      },
      yAxis: {
        type: 'category', data: yLabels, splitArea: { show: true },
        axisLabel: { color: tc.textColor, fontSize: 11 },
      },
      visualMap: {
        min: 0, max: maxVal, calculable: false, show: false,
        inRange: { color: [colorRange[0], '#facc15', colorRange[1]] },
      },
      series: [{
        type: 'heatmap', data,
        label: { show: true, color: '#fff', fontSize: 11, fontWeight: 600 },
        itemStyle: { borderWidth: 2, borderColor: tc.bg },
      }],
    });

    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(chartRef.current);
    return () => { ro.disconnect(); chart.dispose(); };
  }, [xLabels, yLabels, data, colorRange, ct]);

  return <div ref={chartRef} className={cn('w-full', className)} style={{ height }} />;
}

export { NNHeatmapWidget };
